/**
 * Secure cookie utilities for token management
 * These functions handle httpOnly cookies via server-side setting
 * and provide fallback for development
 */

// ── Token obfuscation (sync XOR + base64 — keeps all callers synchronous) ────
const _KEY = "hrms_ak_v1_2025x";

function _obfuscate(str) {
  try {
    const out = Array.from(str).map((ch, i) =>
      String.fromCharCode(ch.charCodeAt(0) ^ _KEY.charCodeAt(i % _KEY.length)),
    );
    return btoa(out.join(""));
  } catch {
    return str;
  }
}

function _deobfuscate(stored) {
  try {
    const decoded = atob(stored);
    return Array.from(decoded)
      .map((ch, i) =>
        String.fromCharCode(
          ch.charCodeAt(0) ^ _KEY.charCodeAt(i % _KEY.length),
        ),
      )
      .join("");
  } catch {
    return stored;
  }
}

// Client-side cookie utilities for non-sensitive data
export const setCookie = (name, value, options = {}) => {
  const isDevelopment = import.meta.env.DEV;

  if (isDevelopment) {
    // In development, we can set cookies client-side for testing
    const {
      expires = null,
      maxAge = null,
      domain = null,
      path = "/",
      secure = false,
      httpOnly = false,
      sameSite = "strict",
    } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (expires) {
      cookieString += `; expires=${expires.toUTCString()}`;
    }

    if (maxAge) {
      cookieString += `; max-age=${maxAge}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    cookieString += `; path=${path}`;

    if (secure) {
      cookieString += "; secure";
    }

    if (httpOnly) {
      console.warn("httpOnly cookies can only be set server-side");
    }

    cookieString += `; samesite=${sameSite}`;

    document.cookie = cookieString;
  } else {
    // In production, tokens should be set via httpOnly cookies from server
    console.warn("Secure cookies should be set server-side in production");
  }
};

export const getCookie = (name) => {
  const nameEQ = encodeURIComponent(name) + "=";
  const ca = document.cookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }

  return null;
};

export const deleteCookie = (name, options = {}) => {
  setCookie(name, "", {
    ...options,
    maxAge: -1,
  });
};

// Token-specific functions (these will be server-side in production)
export const getAccessToken = () => {
  const stored = localStorage.getItem("_ak") || getCookie("access_token");
  if (!stored) return null;
  return _deobfuscate(stored);
};

export const getRefreshToken = () => {
  const stored = localStorage.getItem("_rk") || getCookie("refresh_token");
  if (!stored) return null;
  return _deobfuscate(stored);
};

export const clearTokens = () => {
  localStorage.removeItem("_ak");
  localStorage.removeItem("_rk");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  deleteCookie("access_token");
  deleteCookie("refresh_token");
};

export const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem("_ak", _obfuscate(accessToken));
  localStorage.setItem("_rk", _obfuscate(refreshToken));
};
